import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
} from 'react';

import classNames from 'classnames';
import { DriscollApplicationShell } from 'mastodon/features/driscoll/home';
import { useIdentity } from 'mastodon/identity_context';

import type { List, Record } from 'immutable';

import { useAppSelector } from '@/mastodon/store';

import {
  Compose,
  Notifications,
  HomeTimeline,
  CommunityTimeline,
  PublicTimeline,
  HashtagTimeline,
  DirectTimeline,
  FavouritedStatuses,
  BookmarkedStatuses,
  ListTimeline,
  Directory,
} from '../util/async-components';

import Bundle from './bundle';
import { BundleColumnError } from './bundle_column_error';
import { ColumnLoading } from './column_loading';
import { RedirectToMobileComposeIfNeeded } from './compose_panel';
import DrawerLoading from './drawer_loading';

const componentMap = {
  COMPOSE: Compose,
  HOME: HomeTimeline,
  NOTIFICATIONS: Notifications,
  PUBLIC: PublicTimeline,
  REMOTE: PublicTimeline,
  COMMUNITY: CommunityTimeline,
  HASHTAG: HashtagTimeline,
  DIRECT: DirectTimeline,
  FAVOURITES: FavouritedStatuses,
  BOOKMARKS: BookmarkedStatuses,
  LIST: ListTimeline,
  DIRECTORY: Directory,
} as const;

export const ColumnIndexContext = createContext(1);
export const useColumnIndexContext = () => useContext(ColumnIndexContext);

interface Column {
  uuid: string;
  id: keyof typeof componentMap;
  params?: null | Record<{ other?: unknown }>;
}

type FetchedComponent = React.FC<{
  columnId?: string;
  multiColumn?: boolean;
  params: unknown;
}>;

export const ColumnsArea = forwardRef<
  HTMLDivElement,
  {
    singleColumn?: boolean;
    minimalShell?: boolean;
    children: React.ReactElement | React.ReactElement[];
  }
>(({ children, minimalShell, singleColumn }, ref) => {
  const { signedIn } = useIdentity();
  const columns = useAppSelector(
    (state) => state.settings.get('columns') as List<Record<Column>>,
  );
  const isModalOpen = useAppSelector(
    (state) => !state.modal.get('stack').isEmpty(),
  );

  if (minimalShell || singleColumn) {
    return (
      <DriscollApplicationShell signedIn={signedIn}>
        {signedIn && <RedirectToMobileComposeIfNeeded />}
        {children}
      </DriscollApplicationShell>
    );
  }

  /* The advanced deck keeps its independently scrollable columns, pin/reorder
     controls and compose panel. It shares the brand shell and design tokens. */
  return (
    <DriscollApplicationShell signedIn={signedIn} advanced>
      {signedIn && <RedirectToMobileComposeIfNeeded />}
      <div
        className={classNames('columns-area', { unscrollable: isModalOpen })}
        ref={ref}
        tabIndex={isModalOpen ? undefined : 0}
      >
        {columns.map((column, index) => {
          const params = column.get('params') ? column.get('params')?.toJS() : null;
          const other = params?.other ?? {};
          const uuid = column.get('uuid');
          const id = column.get('id');
          return (
            <ColumnIndexContext.Provider value={index} key={uuid}>
              <Bundle key={uuid} fetchComponent={componentMap[id]} loading={renderLoading(id)} error={ErrorComponent}>
                {(SpecificComponent: FetchedComponent) => (
                  <SpecificComponent columnId={uuid} params={params} multiColumn {...other} />
                )}
              </Bundle>
            </ColumnIndexContext.Provider>
          );
        })}
        <ColumnIndexContext.Provider value={columns.size}>
          {Children.map(children, (child) =>
            isValidElement<{ multiColumn?: boolean }>(child)
              ? cloneElement(child, { multiColumn: true }) : child,
          )}
        </ColumnIndexContext.Provider>
      </div>
    </DriscollApplicationShell>
  );
});

ColumnsArea.displayName = 'ColumnsArea';

const ErrorComponent = (props: { onRetry: () => void }) => {
  return <BundleColumnError multiColumn errorType='network' {...props} />;
};

const renderLoading = (columnId: string) => {
  const LoadingComponent =
    columnId === 'COMPOSE' ? <DrawerLoading /> : <ColumnLoading multiColumn />;
  return () => LoadingComponent;
};
